import React, { useCallback, useId } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Button from 'reactstrap/lib/Button';
import Col from 'reactstrap/lib/Col';
import { LEASE_DEALS_FILTERS } from 'site-modules/lease-deals/constants/lease-deals-constants';
import { ContentCollapse } from 'site-modules/shared/components/content-collapse/content-collapse';

import './collapsible-filters-group.scss';

export const CollapsibleFiltersGroup = ({
  children,
  className,
  hideCollapseSeparator,
  textClosed,
  textOpen,
  trackingId,
  collapseTrackingId,
  trackingValue,
  isOpenOnLoad,
  btnContainerClassName,
  collapseClassName,
  titleTag: TitleTag = 'h3',
}) => {
  const id = useId();
  const collapseControlTitleId = `collapsible-filters-group-${id}`;

  const renderCollapseControl = useCallback(
    ({ isOpen, onToggle }) => (
      <Col className="pb-1 px-1_5">
        <TitleTag className="d-flex align-items-center justify-content-between mb-0">
          <Button
            className="content-collapse-button pos-a top-0 right-0 left-0 bottom-0 rounded-6"
            color="link"
            onClick={onToggle}
            data-no-refresh
            aria-expanded={isOpen}
            aria-labelledby={collapseControlTitleId}
            data-tracking-id={isOpen ? collapseTrackingId : trackingId}
            data-tracking-value={trackingValue}
          />
          <span id={collapseControlTitleId} className="text-cool-gray-10 size-20 fw-medium">
            {isOpen ? textOpen : textClosed}
          </span>
          <span
            className={classnames('text-cool-gray-40 small', isOpen ? 'icon-arrow-up3' : 'icon-arrow-down3')}
            aria-hidden
          />
        </TitleTag>
      </Col>
    ),
    [collapseControlTitleId, collapseTrackingId, textClosed, textOpen, trackingId, trackingValue]
  );

  const collapseChildrenRenderProp = useCallback(
    () => state => {
      if (!children) return null;
      return state !== 'collapsed' && <div className={collapseClassName}>{children}</div>;
    },

    [children, collapseClassName]
  );

  return (
    <div className={classnames('collapsible-filters-group pt-1', className)} data-tracking-parent={LEASE_DEALS_FILTERS}>
      <ContentCollapse
        textClosed={textClosed}
        textOpen={textOpen}
        justify="justify-content-start"
        btnContainerClasses={classnames('pos-r mx-0', btnContainerClassName)}
        isEclipseFade={false}
        trackingId={null}
        isOpenOnLoad={isOpenOnLoad}
        renderCollapseControl={renderCollapseControl}
      >
        {collapseChildrenRenderProp}
      </ContentCollapse>

      {!hideCollapseSeparator && <hr className="separator m-0 mx-1_5" />}
    </div>
  );
};

CollapsibleFiltersGroup.propTypes = {
  children: PropTypes.node,
  textClosed: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  textOpen: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  trackingId: PropTypes.string,
  collapseTrackingId: PropTypes.string,
  trackingValue: PropTypes.string,
  className: PropTypes.string,
  btnContainerClassName: PropTypes.string,
  collapseClassName: PropTypes.string,
  hideCollapseSeparator: PropTypes.bool,
  isOpenOnLoad: PropTypes.bool,
  titleTag: PropTypes.string,
};
